import {
  UploadCourseImportFile,
  PreviewCourseImportFile,
  AnalyzeCourseImportFile,
  saveCourseImportMapping,
  getCourseImportMappings,
  getCourseImportMapping,
  deleteCourseImportMapping,
  startCourseImport,
  getCourseImportStatus,
} from "../../DriveScout/API/models/CourseImportWizard";

const state = {
  columnMapping: {},
  currentPage: 1,
  rowsPerPage: 5,
  currentStep: 1,
  fileUploaded: false,
  fileDirectory: "",
  fileName: "",
  csvHeaders: [],
  previewData: [],
  availableFields: [],
  importOptions: {
    test: false,
  },
  importing: false,
  importResults: null,
  file: null,
  isDragging: false,
  loading: false,
  error: null,
  requiredFields: [],
  // Course type mapping
  availableCourseTypes: [],
  uniqueCourseTypeValues: [],
  courseTypeMapping: {},
  showCourseTypeMappingStep: false,
  // Instructor mapping
  availableInstructors: [],
  uniqueInstructorValues: [],
  instructorMapping: {},
  showInstructorMappingStep: false,
  // Loading states
  analyzingFile: false,
  // Async import progress tracking
  importId: null,
  importStatus: null, // pending, processing, completed, failed
  importProgress: {
    total: 0,
    processed: 0,
    percentage: 0,
  },
  importFailureReason: null,
};

const mutations = {
  SET_COURSE_IMPORT_COLUMN_MAPPING(state, mapping) {
    state.columnMapping = mapping;
  },
  SET_COURSE_IMPORT_PAGE(state, page) {
    state.currentPage = page;
  },
  SET_COURSE_IMPORT_STEP(state, step) {
    state.currentStep = step;
  },
  SET_COURSE_IMPORT_FILE_UPLOADED(state, uploaded) {
    state.fileUploaded = uploaded;
  },
  SET_COURSE_IMPORT_FILE_INFO(state, { directory, name }) {
    state.fileDirectory = directory;
    state.fileName = name;
  },
  SET_COURSE_IMPORT_PREVIEW_DATA(state, { headers, data, fields }) {
    state.csvHeaders = headers;
    state.previewData = data;
    state.availableFields = fields;
  },
  SET_COURSE_IMPORT_OPTIONS(state, options) {
    state.importOptions = { ...options };
  },
  SET_COURSE_IMPORT_IMPORTING(state, importing) {
    state.importing = importing;
  },
  SET_COURSE_IMPORT_RESULTS(state, results) {
    state.importResults = results;
  },
  RESET_COURSE_IMPORT(state) {
    Object.assign(state, {
      columnMapping: {},
      currentPage: 1,
      rowsPerPage: 5,
      currentStep: 1,
      fileUploaded: false,
      fileDirectory: "",
      fileName: "",
      csvHeaders: [],
      previewData: [],
      availableFields: [],
      importOptions: {
        test: false,
      },
      importing: false,
      importResults: null,
      file: null,
      isDragging: false,
      loading: false,
      error: null,
      requiredFields: [],
      // Reset course type mapping state
      availableCourseTypes: [],
      uniqueCourseTypeValues: [],
      courseTypeMapping: {},
      showCourseTypeMappingStep: false,
      // Reset instructor mapping state
      availableInstructors: [],
      uniqueInstructorValues: [],
      instructorMapping: {},
      showInstructorMappingStep: false,
      analyzingFile: false,
      // Reset async import state
      importId: null,
      importStatus: null,
      importProgress: {
        total: 0,
        processed: 0,
        percentage: 0,
      },
      importFailureReason: null,
    });
  },
  SET_COURSE_IMPORT_FILE(state, file) {
    state.file = file;
  },
  SET_COURSE_IMPORT_DRAGGING(state, isDragging) {
    state.isDragging = isDragging;
  },
  SET_COURSE_IMPORT_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_COURSE_IMPORT_ERROR(state, error) {
    state.error = error;
  },
  SET_COURSE_IMPORT_REQUIRED_FIELDS(state, fields) {
    state.requiredFields = fields;
  },
  // Course type mutations
  SET_COURSE_IMPORT_AVAILABLE_COURSE_TYPES(state, courseTypes) {
    state.availableCourseTypes = courseTypes;
  },
  SET_COURSE_IMPORT_UNIQUE_COURSE_TYPE_VALUES(state, values) {
    state.uniqueCourseTypeValues = values;
  },
  SET_COURSE_IMPORT_COURSE_TYPE_MAPPING(state, mapping) {
    state.courseTypeMapping = mapping;
  },
  SET_COURSE_IMPORT_SHOW_COURSE_TYPE_MAPPING_STEP(state, show) {
    state.showCourseTypeMappingStep = show;
  },
  // Instructor mutations
  SET_COURSE_IMPORT_AVAILABLE_INSTRUCTORS(state, instructors) {
    state.availableInstructors = instructors;
  },
  SET_COURSE_IMPORT_UNIQUE_INSTRUCTOR_VALUES(state, values) {
    state.uniqueInstructorValues = values;
  },
  SET_COURSE_IMPORT_INSTRUCTOR_MAPPING(state, mapping) {
    state.instructorMapping = mapping;
  },
  SET_COURSE_IMPORT_SHOW_INSTRUCTOR_MAPPING_STEP(state, show) {
    state.showInstructorMappingStep = show;
  },
  SET_COURSE_IMPORT_ANALYZING_FILE(state, analyzing) {
    state.analyzingFile = analyzing;
  },
  SET_COURSE_IMPORT_ID(state, importId) {
    state.importId = importId;
  },
  SET_COURSE_IMPORT_STATUS(state, status) {
    state.importStatus = status;
  },
  SET_COURSE_IMPORT_PROGRESS(state, progress) {
    state.importProgress = { ...progress };
  },
  SET_COURSE_IMPORT_FAILURE_REASON(state, reason) {
    state.importFailureReason = reason;
  },
};

const actions = {
  PREVIEW_COURSE_IMPORT_FILE({ commit }, data) {
    return PreviewCourseImportFile(data.school_id, data.file_id).then(
      (response) => {
        return response;
      },
    );
  },
  UPLOAD_COURSE_IMPORT_FILE({ commit }, data) {
    return UploadCourseImportFile(data.school_id, data.formData).then(
      (response) => {
        return response;
      },
    );
  },
  SAVE_COURSE_IMPORT_MAPPING({ commit }, data) {
    return saveCourseImportMapping(
      data.school_id,
      data.name,
      data.mapping,
    ).then((response) => {
      return response;
    });
  },
  GET_COURSE_IMPORT_MAPPINGS({ commit }, data) {
    return getCourseImportMappings(data.school_id).then((response) => {
      return response;
    });
  },
  GET_COURSE_IMPORT_MAPPING({ commit }, data) {
    return getCourseImportMapping(data.school_id, data.mapping_id).then(
      (response) => {
        return response;
      },
    );
  },
  DELETE_COURSE_IMPORT_MAPPING({ commit }, data) {
    return deleteCourseImportMapping(data.school_id, data.mapping_id).then(
      (response) => {
        return response;
      },
    );
  },
  ANALYZE_COURSE_IMPORT_FILE({ commit }, data) {
    commit("SET_COURSE_IMPORT_ANALYZING_FILE", true);
    return AnalyzeCourseImportFile(
      data.school_id,
      data.file_id,
      data.column_mapping,
    )
      .then((response) => {
        return response;
      })
      .finally(() => {
        commit("SET_COURSE_IMPORT_ANALYZING_FILE", false);
      });
  },
  START_COURSE_IMPORT({ commit }, data) {
    return startCourseImport(
      data.school_id,
      data.file_id,
      data.test,
      data.column_mapping,
      data.course_type_mapping,
      data.instructor_mapping,
    ).then((response) => {
      return response;
    });
  },
  GET_COURSE_IMPORT_STATUS({ commit }, data) {
    return getCourseImportStatus(data.school_id, data.import_id).then(
      (response) => {
        const statusData = response.data;
        commit("SET_COURSE_IMPORT_STATUS", statusData.status);
        commit("SET_COURSE_IMPORT_PROGRESS", statusData.progress);
        if (statusData.failure_reason) {
          commit("SET_COURSE_IMPORT_FAILURE_REASON", statusData.failure_reason);
        }
        if (statusData.results) {
          commit("SET_COURSE_IMPORT_RESULTS", statusData.results);
        }
        return response;
      },
    );
  },
  SET_COURSE_IMPORT_COLUMN_MAPPING({ commit }, mapping) {
    commit("SET_COURSE_IMPORT_COLUMN_MAPPING", mapping);
  },
  SET_COURSE_IMPORT_PAGE({ commit }, page) {
    commit("SET_COURSE_IMPORT_PAGE", page);
  },
  SET_COURSE_IMPORT_STEP({ commit }, step) {
    commit("SET_COURSE_IMPORT_STEP", step);
  },
  SET_COURSE_IMPORT_FILE_UPLOADED({ commit }, uploaded) {
    commit("SET_COURSE_IMPORT_FILE_UPLOADED", uploaded);
  },
  SET_COURSE_IMPORT_FILE_INFO({ commit }, info) {
    commit("SET_COURSE_IMPORT_FILE_INFO", info);
  },
  SET_COURSE_IMPORT_PREVIEW_DATA({ commit }, data) {
    commit("SET_COURSE_IMPORT_PREVIEW_DATA", data);
  },
  SET_COURSE_IMPORT_OPTIONS({ commit }, options) {
    commit("SET_COURSE_IMPORT_OPTIONS", options);
  },
  SET_COURSE_IMPORT_IMPORTING({ commit }, importing) {
    commit("SET_COURSE_IMPORT_IMPORTING", importing);
  },
  SET_COURSE_IMPORT_RESULTS({ commit }, results) {
    commit("SET_COURSE_IMPORT_RESULTS", results);
  },
  RESET_COURSE_IMPORT({ commit }) {
    commit("RESET_COURSE_IMPORT");
  },
  SET_COURSE_IMPORT_FILE({ commit }, file) {
    commit("SET_COURSE_IMPORT_FILE", file);
  },
  SET_COURSE_IMPORT_DRAGGING({ commit }, isDragging) {
    commit("SET_COURSE_IMPORT_DRAGGING", isDragging);
  },
  SET_COURSE_IMPORT_LOADING({ commit }, loading) {
    commit("SET_COURSE_IMPORT_LOADING", loading);
  },
  SET_COURSE_IMPORT_ERROR({ commit }, error) {
    commit("SET_COURSE_IMPORT_ERROR", error);
  },
  SET_COURSE_IMPORT_REQUIRED_FIELDS({ commit }, fields) {
    commit("SET_COURSE_IMPORT_REQUIRED_FIELDS", fields);
  },
  // Course type actions
  SET_COURSE_IMPORT_AVAILABLE_COURSE_TYPES({ commit }, courseTypes) {
    commit("SET_COURSE_IMPORT_AVAILABLE_COURSE_TYPES", courseTypes);
  },
  SET_COURSE_IMPORT_UNIQUE_COURSE_TYPE_VALUES({ commit }, values) {
    commit("SET_COURSE_IMPORT_UNIQUE_COURSE_TYPE_VALUES", values);
  },
  SET_COURSE_IMPORT_COURSE_TYPE_MAPPING({ commit }, mapping) {
    commit("SET_COURSE_IMPORT_COURSE_TYPE_MAPPING", mapping);
  },
  SET_COURSE_IMPORT_SHOW_COURSE_TYPE_MAPPING_STEP({ commit }, show) {
    commit("SET_COURSE_IMPORT_SHOW_COURSE_TYPE_MAPPING_STEP", show);
  },
  // Instructor actions
  SET_COURSE_IMPORT_AVAILABLE_INSTRUCTORS({ commit }, instructors) {
    commit("SET_COURSE_IMPORT_AVAILABLE_INSTRUCTORS", instructors);
  },
  SET_COURSE_IMPORT_UNIQUE_INSTRUCTOR_VALUES({ commit }, values) {
    commit("SET_COURSE_IMPORT_UNIQUE_INSTRUCTOR_VALUES", values);
  },
  SET_COURSE_IMPORT_INSTRUCTOR_MAPPING({ commit }, mapping) {
    commit("SET_COURSE_IMPORT_INSTRUCTOR_MAPPING", mapping);
  },
  SET_COURSE_IMPORT_SHOW_INSTRUCTOR_MAPPING_STEP({ commit }, show) {
    commit("SET_COURSE_IMPORT_SHOW_INSTRUCTOR_MAPPING_STEP", show);
  },
  // Async import actions
  SET_COURSE_IMPORT_ID({ commit }, importId) {
    commit("SET_COURSE_IMPORT_ID", importId);
  },
  SET_COURSE_IMPORT_STATUS({ commit }, status) {
    commit("SET_COURSE_IMPORT_STATUS", status);
  },
  SET_COURSE_IMPORT_PROGRESS({ commit }, progress) {
    commit("SET_COURSE_IMPORT_PROGRESS", progress);
  },
  SET_COURSE_IMPORT_FAILURE_REASON({ commit }, reason) {
    commit("SET_COURSE_IMPORT_FAILURE_REASON", reason);
  },
};

const getters = {
  courseImportColumnMapping: (state) => state.columnMapping,
  courseImportCurrentPage: (state) => state.currentPage,
  courseImportRowsPerPage: (state) => state.rowsPerPage,
  courseImportCurrentStep: (state) => state.currentStep,
  courseImportFileUploaded: (state) => state.fileUploaded,
  courseImportFileInfo: (state) => ({
    directory: state.fileDirectory,
    name: state.fileName,
  }),
  courseImportPreviewData: (state) => ({
    headers: state.csvHeaders,
    data: state.previewData,
    fields: state.availableFields,
  }),
  courseImportOptions: (state) => state.importOptions,
  courseImportImporting: (state) => state.importing,
  courseImportResults: (state) => state.importResults,
  courseImportFile: (state) => state.file,
  courseImportDragging: (state) => state.isDragging,
  courseImportLoading: (state) => state.loading,
  courseImportError: (state) => state.error,
  courseImportRequiredFields: (state) => state.requiredFields,
  courseImportMappedPreviewData: (state) => {
    if (!state.previewData.length) {
      return [];
    }

    return state.previewData.map((row) => {
      const mappedRow = {};
      Object.keys(state.columnMapping).forEach((csvCol) => {
        const systemField = state.columnMapping[csvCol];
        if (systemField) {
          mappedRow[systemField] = row[csvCol];
        }
      });
      return mappedRow;
    });
  },
  validateCourseImportMapping: (state) => {
    const mappedFields = Object.values(state.columnMapping);
    return state.requiredFields.every((field) => mappedFields.includes(field));
  },
  // Course type getters
  courseImportAvailableCourseTypes: (state) => state.availableCourseTypes,
  courseImportUniqueCourseTypeValues: (state) => state.uniqueCourseTypeValues,
  courseImportCourseTypeMapping: (state) => state.courseTypeMapping,
  courseImportShowCourseTypeMappingStep: (state) =>
    state.showCourseTypeMappingStep,
  // Instructor getters
  courseImportAvailableInstructors: (state) => state.availableInstructors,
  courseImportUniqueInstructorValues: (state) => state.uniqueInstructorValues,
  courseImportInstructorMapping: (state) => state.instructorMapping,
  courseImportShowInstructorMappingStep: (state) =>
    state.showInstructorMappingStep,
  courseImportAnalyzingFile: (state) => state.analyzingFile,
  // Validates that all unique course type values have been mapped
  validateCourseImportCourseTypeMapping: (state) => {
    if (!state.showCourseTypeMappingStep) {
      return true;
    }
    if (state.uniqueCourseTypeValues.length === 0) {
      return true;
    }
    return state.uniqueCourseTypeValues.every(
      (item) => state.courseTypeMapping[item.value] !== undefined,
    );
  },
  // Validates that all unique instructor values have been mapped
  validateCourseImportInstructorMapping: (state) => {
    if (!state.showInstructorMappingStep) {
      return true;
    }
    if (state.uniqueInstructorValues.length === 0) {
      return true;
    }
    return state.uniqueInstructorValues.every(
      (item) => state.instructorMapping[item.value] !== undefined,
    );
  },
  // Async import getters
  courseImportId: (state) => state.importId,
  courseImportStatus: (state) => state.importStatus,
  courseImportProgress: (state) => state.importProgress,
  courseImportFailureReason: (state) => state.importFailureReason,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
